import { apiRoute } from "./config";
import {
  fingerprintPrivateSummary,
  safeExternalURL,
  type AudienceAssignment,
  type Bookmark,
  type LibraryFormat,
  type LibraryLane,
  type LibraryRecipientEntry,
  type LibraryReceipt,
  type LibraryStatus,
  type VectorStatus,
} from "./domain";
import {
  type AuthoritativeConversationCard,
  type AuthoritativeConversationCleanupPointer,
  type AuthoritativeConversationEndReason,
  type AuthoritativeConversationEvent,
  type AuthoritativeConversationMutation,
  type AuthoritativeConversationRepository,
  type AuthoritativeConversationSession,
  type AuthoritativeConversationSessions,
  type BookmarkCreateRepository,
  type BookmarkCreateResult,
  type BookmarkRepository,
  type BookmarkSaveResult,
  type CommandAuthoritativeConversationInput,
  type OwnerBookmarkWorkspace,
  type OwnerWorkspaceAudience,
  type OwnerWorkspaceContext,
  type RecipientLibraryRepository,
  type StartAuthoritativeConversationInput,
  type SummaryEnrichmentJob,
  type WorkspaceLoadResult,
} from "./repository";

type JSONRecord = Record<string, unknown>;
type Fetcher = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

interface CachedOwnerWorkspace {
  workspace: OwnerBookmarkWorkspace;
  context: OwnerWorkspaceContext;
  revision: number;
  etag: string;
}

interface ReceiptTransportState {
  etag: string;
  revision: number;
}

export class CloudflareRepositoryError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly currentETag: string | null = null,
  ) {
    super(message);
  }
}

function objectValue(value: unknown, scope: string): JSONRecord {
  if (!value || Array.isArray(value) || typeof value !== "object") {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value as JSONRecord;
}

function arrayValue(value: unknown, scope: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function stringValue(value: unknown, scope: string): string {
  if (typeof value !== "string") {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function nonEmptyString(value: unknown, scope: string): string {
  const result = stringValue(value, scope);
  if (!result.trim()) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return result;
}

function nullableString(value: unknown, scope: string): string | null {
  return value === null ? null : stringValue(value, scope);
}

function booleanValue(value: unknown, scope: string): boolean {
  if (typeof value !== "boolean") {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function integerValue(value: unknown, scope: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 0) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return Number(value);
}

function optionalNonnegativeNumber(
  value: unknown,
  scope: string,
): number | undefined {
  if (value === undefined) return undefined;
  if (
    typeof value !== "number"
    || !Number.isFinite(value)
    || value < 0
  ) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function stringList(value: unknown, scope: string): string[] {
  return arrayValue(value, scope).map((item, index) => (
    stringValue(item, `${scope}[${index}]`)
  ));
}

function formatValue(value: unknown, scope: string): LibraryFormat {
  if (
    value !== "text"
    && value !== "audio"
    && value !== "video"
    && value !== "unknown"
  ) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function laneValue(value: unknown, scope: string): LibraryLane {
  if (value !== "read" && value !== "listen" && value !== "watch") {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function statusValue(value: unknown, scope: string): LibraryStatus {
  if (
    value !== "new"
    && value !== "open"
    && value !== "in_progress"
    && value !== "finished"
    && value !== "dismissed"
  ) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function rejectForbiddenKeysDeep(
  value: unknown,
  keys: readonly string[],
  scope: string,
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      rejectForbiddenKeysDeep(item, keys, `${scope}[${index}]`);
    });
    return;
  }
  if (!value || typeof value !== "object") return;
  for (const [key, nested] of Object.entries(value)) {
    if (keys.includes(key)) {
      throw new CloudflareRepositoryError(
        `${scope} included an owner-private field.`,
        502,
        "private_projection_violation",
      );
    }
    rejectForbiddenKeysDeep(nested, keys, `${scope}.${key}`);
  }
}

async function jsonRecord(response: Response): Promise<JSONRecord> {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (!contentType.includes("application/json")) {
    throw new CloudflareRepositoryError(
      "Convoful returned an unexpected response.",
      502,
      "invalid_server_response",
    );
  }
  return objectValue(await response.json(), "Convoful response");
}

async function responseError(
  response: Response,
  fallback: string,
): Promise<CloudflareRepositoryError> {
  const body: JSONRecord = await jsonRecord(response).catch(() => ({}));
  const code = typeof body.error === "string" && body.error
    ? body.error
    : "request_failed";
  const currentETag = typeof body.current_etag === "string"
    ? body.current_etag
    : null;
  let message = fallback;
  if (response.status === 401) {
    message = "This browser’s private access has expired. Return to sign in and send yourself a new email link.";
  } else if (response.status === 403) {
    message = "This browser no longer has access to that private space.";
  } else if (response.status === 404) {
    message = "That Library item is no longer available.";
  } else if (response.status === 409) {
    message = "This item changed elsewhere. Refresh before trying again.";
  } else if (response.status === 428) {
    message = "Refresh this private workspace before saving.";
  }
  return new CloudflareRepositoryError(
    message,
    response.status,
    code,
    currentETag,
  );
}

function assignmentPresentationID(audienceID: string): string {
  return `owner-assignment:${audienceID}`;
}

const CONVERSATION_IDENTIFIER = /^[A-Za-z0-9._:@+-]{1,160}$/;

function conversationIdentifier(value: unknown, scope: string): string {
  const result = nonEmptyString(value, scope);
  if (!CONVERSATION_IDENTIFIER.test(result)) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return result;
}

function audienceContext(value: unknown, index: number): OwnerWorkspaceAudience {
  const row = objectValue(value, `audiences[${index}]`);
  return {
    id: nonEmptyString(row.id, `audiences[${index}].id`),
    label: nonEmptyString(
      row.display_name,
      `audiences[${index}].display_name`,
    ),
  };
}

function publicationFromWire(
  value: unknown,
  assignmentID: string,
  ownerUserID: string,
  scope: string,
): Bookmark["libraryPublications"][number] | null {
  if (value === null) return null;
  const row = objectValue(value, scope);
  rejectForbiddenKeysDeep(
    row,
    [
      "card_id",
      "canonical_card_id",
      "private_notes",
      "human_summary",
      "keywords",
      "tags",
      "payload",
      "summary_fingerprint",
      "summary_embedding",
    ],
    scope,
  );
  const enabled = booleanValue(row.enabled, `${scope}.enabled`);
  const videoDurationSeconds = optionalNonnegativeNumber(
    row.video_duration_seconds === null
      ? undefined
      : row.video_duration_seconds,
    `${scope}.video_duration_seconds`,
  );
  return {
    id: nonEmptyString(row.id, `${scope}.id`),
    assignmentId: assignmentID,
    sharedByUserID: ownerUserID,
    sharedTitle: nonEmptyString(row.shared_title, `${scope}.shared_title`),
    sharedURL: nullableString(row.shared_url, `${scope}.shared_url`),
    sharedSourceLabel: stringValue(
      row.shared_source_label,
      `${scope}.shared_source_label`,
    ),
    sharedNotes: stringValue(row.shared_notes, `${scope}.shared_notes`),
    format: formatValue(row.format, `${scope}.format`),
    lane: laneValue(row.lane, `${scope}.lane`),
    ...(videoDurationSeconds === undefined
      ? {}
      : { videoDurationSeconds }),
    publishedAt: nonEmptyString(row.published_at, `${scope}.published_at`),
    updatedAt: nonEmptyString(row.updated_at, `${scope}.updated_at`),
    revokedAt: enabled
      ? null
      : nonEmptyString(row.updated_at, `${scope}.updated_at`),
  };
}

function vectorStatus(row: JSONRecord): VectorStatus {
  const status = nonEmptyString(row.summary_status, "bookmark.summary_status");
  if (status === "failed") return "failed";
  return status === "complete"
      && booleanValue(
        row.summary_embedding_current,
        "bookmark.summary_embedding_current",
      )
    ? "ready"
    : "pending";
}

function bookmarkFromWire(
  value: unknown,
  ownerUserID: string,
  audienceLabels: Map<string, string>,
  index: number,
  seenAssignmentIDs: Set<string> = new Set(),
): Bookmark {
  const scope = `bookmarks[${index}]`;
  const row = objectValue(value, scope);
  const assignments: AudienceAssignment[] = [];
  const publications: Bookmark["libraryPublications"] = [];
  for (const [assignmentIndex, rawAssignment] of arrayValue(
    row.assignments,
    `${scope}.assignments`,
  ).entries()) {
    const assignmentScope = `${scope}.assignments[${assignmentIndex}]`;
    const assignment = objectValue(rawAssignment, assignmentScope);
    const audienceID = nonEmptyString(
      assignment.audience_id,
      `${assignmentScope}.audience_id`,
    );
    if (booleanValue(
      assignment.cleanup_only,
      `${assignmentScope}.cleanup_only`,
    )) {
      continue;
    }
    const assignmentID = conversationIdentifier(
      assignment.assignment_id,
      `${assignmentScope}.assignment_id`,
    );
    if (seenAssignmentIDs.has(assignmentID)) {
      throw new CloudflareRepositoryError(
        `${assignmentScope}.assignment_id is duplicated.`,
        502,
        "invalid_server_response",
      );
    }
    seenAssignmentIDs.add(assignmentID);
    assignments.push({
      id: assignmentID,
      audienceId: audienceID,
      audienceLabel: audienceLabels.get(audienceID) ?? audienceID,
      conversationEnabled: booleanValue(
        assignment.conversation_enabled,
        `${assignmentScope}.conversation_enabled`,
      ),
      libraryEnabled: booleanValue(
        assignment.library_enabled,
        `${assignmentScope}.library_enabled`,
      ),
      isPrimary: booleanValue(
        assignment.is_primary,
        `${assignmentScope}.is_primary`,
      ),
      conversationState: "available",
    });
    const publication = publicationFromWire(
      assignment.publication,
      assignmentID,
      ownerUserID,
      `${assignmentScope}.publication`,
    );
    if (publication) publications.push(publication);
  }
  const assignedAudienceIDs = new Set(
    assignments.map(({ audienceId }) => audienceId),
  );
  for (const [audienceID, audienceLabel] of audienceLabels) {
    if (assignedAudienceIDs.has(audienceID)) continue;
    assignments.push({
      id: assignmentPresentationID(audienceID),
      audienceId: audienceID,
      audienceLabel,
      conversationEnabled: false,
      libraryEnabled: false,
      isPrimary: false,
      conversationState: "available",
    });
  }
  const livePublication = publications.find(({ revokedAt }) => revokedAt === null);
  const privateSummary = nullableString(
    row.human_summary,
    `${scope}.human_summary`,
  ) ?? "";
  const embeddingCurrent = booleanValue(
    row.summary_embedding_current,
    `${scope}.summary_embedding_current`,
  );
  const embeddedSummaryFingerprint = embeddingCurrent
    ? nullableString(
      row.summary_fingerprint,
      `${scope}.summary_fingerprint`,
    )
    : null;
  const source = nonEmptyString(row.source, `${scope}.source`);
  const primaryURL = nullableString(row.primary_url, `${scope}.primary_url`);
  return {
    id: nonEmptyString(row.id, `${scope}.id`),
    title: nonEmptyString(row.title, `${scope}.title`),
    sourceLabel: source,
    sourceURL: primaryURL && safeExternalURL(primaryURL)
      ? primaryURL
      : "",
    senderID: ownerUserID,
    senderLabel: "You",
    audienceAssignments: assignments,
    libraryPublications: publications,
    format: livePublication?.format ?? "unknown",
    lane: livePublication?.lane ?? "read",
    createdAt: nonEmptyString(row.created_at, `${scope}.created_at`),
    ...(livePublication?.videoDurationSeconds === undefined
      ? {}
      : { videoDurationSeconds: livePublication.videoDurationSeconds }),
    primaryTheme: nonEmptyString(row.primary_theme, `${scope}.primary_theme`),
    ...(row.cluster === undefined
      ? {}
      : { cluster: nonEmptyString(row.cluster, `${scope}.cluster`) }),
    privateNotes: stringValue(row.private_notes, `${scope}.private_notes`),
    privateHumanSummary: privateSummary,
    privateSummaryInputMode: privateSummary ? null : null,
    triageAudit: null,
    summaryFingerprint: embeddedSummaryFingerprint
      ?? fingerprintPrivateSummary(privateSummary),
    embeddedSummaryFingerprint,
    vectorStatus: vectorStatus(row),
    keywords: stringList(row.keywords, `${scope}.keywords`),
    tags: stringList(row.tags, `${scope}.tags`),
    privateCardMedia: [],
  };
}

function ownerWorkspaceFromWire(
  value: unknown,
  etag: string,
): CachedOwnerWorkspace {
  const body = objectValue(value, "owner workspace");
  if (body.schema_version !== "owner-workspace-v1") {
    throw new CloudflareRepositoryError(
      "The owner workspace version is not supported.",
      502,
      "unsupported_schema_version",
    );
  }
  const ownerUserID = nonEmptyString(body.owner_user_id, "owner_user_id");
  const audiences = arrayValue(body.audiences, "audiences")
    .map(audienceContext);
  const audienceLabels = new Map(
    audiences.map(({ id, label }) => [id, label]),
  );
  const seenAssignmentIDs = new Set<string>();
  const bookmarks = arrayValue(body.bookmarks, "bookmarks")
    .map((bookmark, index) => (
      bookmarkFromWire(
        bookmark,
        ownerUserID,
        audienceLabels,
        index,
        seenAssignmentIDs,
      )
    ));
  return {
    revision: integerValue(body.revision, "revision"),
    etag,
    context: { ownerUserID, audiences },
    workspace: {
      bookmarks,
      enrichmentJobs: bookmarks.flatMap((bookmark) => (
        bookmark.vectorStatus === "ready" || !bookmark.privateHumanSummary
          ? []
          : [{
            id: `server-summary:${bookmark.id}:${body.revision}`,
            bookmarkId: bookmark.id,
            summaryFingerprint: bookmark.summaryFingerprint,
            state: bookmark.vectorStatus,
          } satisfies SummaryEnrichmentJob]
      )),
    },
  };
}

function assignmentMutation(
  bookmark: Bookmark,
  assignment: AudienceAssignment,
): JSONRecord {
  const value: JSONRecord = {
    audience_id: assignment.audienceId,
    is_primary: assignment.isPrimary,
    conversation_enabled: assignment.conversationEnabled,
    library_enabled: assignment.libraryEnabled,
  };
  if (!assignment.libraryEnabled) return value;
  const livePublications = bookmark.libraryPublications.filter(
    ({ assignmentId, revokedAt }) => (
      assignmentId === assignment.id && revokedAt === null
    ),
  );
  if (livePublications.length !== 1) {
    throw new CloudflareRepositoryError(
      "Every Library destination needs one explicit live publication.",
      400,
      "invalid_library_publication",
    );
  }
  const [publication] = livePublications;
  value.publication = {
    shared_title: publication.sharedTitle,
    shared_url: publication.sharedURL,
    shared_source_label: publication.sharedSourceLabel,
    shared_notes: publication.sharedNotes,
    format: publication.format,
    lane: publication.lane,
    video_duration_seconds: publication.videoDurationSeconds ?? null,
  };
  return value;
}

function ownerMutation(bookmark: Bookmark): JSONRecord {
  const tags = [
    bookmark.primaryTheme,
    ...bookmark.tags.filter((tag) => tag !== bookmark.primaryTheme),
  ];
  const ownerPatch: JSONRecord = {
    private_notes: bookmark.privateNotes,
    keywords: bookmark.keywords,
    tags,
  };
  if (bookmark.privateHumanSummary.trim()) {
    ownerPatch.human_summary = bookmark.privateHumanSummary;
  }
  return {
    owner_patch: ownerPatch,
    assignments: bookmark.audienceAssignments
      .filter(({ conversationEnabled, libraryEnabled }) => (
        conversationEnabled || libraryEnabled
      ))
      .map((assignment) => assignmentMutation(bookmark, assignment)),
  };
}

function ownerCreateMutation(bookmark: Bookmark): JSONRecord {
  const sourceURL = bookmark.sourceURL.trim();
  const safeSourceURL = sourceURL ? safeExternalURL(sourceURL) : null;
  if (sourceURL && !safeSourceURL) {
    throw new CloudflareRepositoryError(
      "The New card source link is not safe to save.",
      400,
      "invalid_owner_source",
    );
  }
  if (!bookmark.privateHumanSummary.trim()) {
    throw new CloudflareRepositoryError(
      "Add a private Summary before saving this New card.",
      400,
      "invalid_owner_summary",
    );
  }
  return {
    source: {
      kind: safeSourceURL ? "link" : "text",
      title: bookmark.title,
      url: safeSourceURL,
    },
    owner: {
      private_notes: bookmark.privateNotes,
      human_summary: bookmark.privateHumanSummary,
      keywords: bookmark.keywords,
      tags: bookmark.tags,
      primary_theme: bookmark.primaryTheme,
    },
    assignments: bookmark.audienceAssignments
      .filter(({ conversationEnabled, libraryEnabled }) => (
        conversationEnabled || libraryEnabled
      ))
      .map((assignment) => assignmentMutation(bookmark, assignment)),
  };
}

function ownerCreateIdempotencyKey(bookmarkID: string): string {
  if (!bookmarkID.startsWith("local-new-card-")) {
    throw new CloudflareRepositoryError(
      "This New card no longer has its recoverable draft identity.",
      400,
      "invalid_owner_create_identity",
    );
  }
  const key = `web-owner-create:${bookmarkID}`;
  if (key.length > 160 || !/^[A-Za-z0-9._:@+-]+$/.test(key)) {
    throw new CloudflareRepositoryError(
      "This New card has an invalid recoverable draft identity.",
      400,
      "invalid_owner_create_identity",
    );
  }
  return key;
}

function summaryEnrichmentResult(
  body: JSONRecord,
  saved: Bookmark,
  revision: number,
): SummaryEnrichmentJob | null {
  const delivery = objectValue(
    body.enrichment_delivery,
    "enrichment_delivery",
  );
  const deliveryState = stringValue(
    delivery.state,
    "enrichment_delivery.state",
  );
  if (
    deliveryState !== "not_needed"
    && deliveryState !== "queued"
    && deliveryState !== "retryable"
  ) {
    throw new CloudflareRepositoryError(
      "The Summary delivery state is not supported.",
      502,
      "invalid_server_response",
    );
  }
  return deliveryState === "not_needed"
    ? null
    : {
      id: `server-summary:${saved.id}:${revision}`,
      bookmarkId: saved.id,
      summaryFingerprint: saved.summaryFingerprint,
      state: deliveryState === "queued" ? "queued" : "failed",
    };
}

const CONVERSATION_SCHEMA = "conversation-sessions-v1";
const CONVERSATION_ETAG =
  /^"conversation-sessions-v1-(0|[1-9][0-9]*)"$/;
const OWNER_WORKSPACE_ETAG = /^"owner-[0-9a-f]{40}"$/;
const CONVERSATION_IDEMPOTENCY_KEY = /^[A-Za-z0-9._:@+-]{1,160}$/;
const ISO_8601_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const CONVERSATION_PRIVATE_KEYS = [
  "prompt",
  "title",
  "source",
  "source_url",
  "private_notes",
  "human_summary",
  "keywords",
  "tags",
  "payload",
  "summary_fingerprint",
  "summary_embedding",
  "embedding",
  "vector",
  "media",
  "publication",
  "receipt",
  "owner_user_id",
  "user_id",
  "principal",
  "created_device_id",
  "device_id",
  "idempotency_key",
  "request_hash",
  "nonce",
] as const;

function nullableInteger(value: unknown, scope: string): number | null {
  return value === null ? null : integerValue(value, scope);
}

function timestampValue(value: unknown, scope: string): string {
  const result = nonEmptyString(value, scope);
  if (
    !ISO_8601_TIMESTAMP.test(result)
    || !Number.isFinite(Date.parse(result))
  ) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return result;
}

function conversationEndReason(
  value: unknown,
  scope: string,
): AuthoritativeConversationEndReason | null {
  if (value === null) return null;
  if (
    value !== "completed"
    && value !== "paused"
    && value !== "abandoned"
  ) {
    throw new CloudflareRepositoryError(
      `${scope} is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  return value;
}

function conversationETag(response: Response): {
  etag: string;
  revision: number;
} {
  const rawETag = response.headers.get("etag") ?? "";
  const etag = rawETag.startsWith("W/") ? rawETag.slice(2) : rawETag;
  const match = CONVERSATION_ETAG.exec(etag);
  if (!match) {
    throw new CloudflareRepositoryError(
      "The Conversation response did not include a valid revision.",
      502,
      "invalid_server_response",
    );
  }
  const revision = Number(match[1]);
  if (!Number.isSafeInteger(revision)) {
    throw new CloudflareRepositoryError(
      "The Conversation response revision was invalid.",
      502,
      "invalid_server_response",
    );
  }
  return { etag, revision };
}

function ownerWorkspaceETag(response: Response): string {
  const rawETag = response.headers.get("etag") ?? "";
  const etag = rawETag.startsWith("W/") ? rawETag.slice(2) : rawETag;
  if (!OWNER_WORKSPACE_ETAG.test(etag)) {
    throw new CloudflareRepositoryError(
      "The private workspace did not include a valid revision.",
      502,
      rawETag ? "invalid_server_response" : "missing_etag",
    );
  }
  return etag;
}

function requirePrivateNoStore(response: Response): void {
  const cacheControl = response.headers.get("cache-control")?.toLowerCase()
    ?? "";
  if (
    !cacheControl.split(",").some((value) => value.trim() === "private")
    || !cacheControl.split(",").some((value) => value.trim() === "no-store")
  ) {
    throw new CloudflareRepositoryError(
      "The Conversation response was missing its private no-store boundary.",
      502,
      "invalid_server_response",
    );
  }
}

function conversationCard(
  value: unknown,
  scope: string,
): AuthoritativeConversationCard {
  const row = objectValue(value, scope);
  const state = row.state;
  if (state !== "playable" && state !== "discussed") {
    throw new CloudflareRepositoryError(
      `${scope}.state is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  const lastEventID = nullableString(row.last_event_id, `${scope}.last_event_id`);
  const lastOutcomeEventID = nullableString(
    row.last_outcome_event_id,
    `${scope}.last_outcome_event_id`,
  );
  return {
    assignmentID: conversationIdentifier(
      row.assignment_id,
      `${scope}.assignment_id`,
    ),
    position: integerValue(row.position, `${scope}.position`),
    state,
    revealed: booleanValue(row.revealed, `${scope}.revealed`),
    lastEventID: lastEventID === null
      ? null
      : conversationIdentifier(lastEventID, `${scope}.last_event_id`),
    lastOutcomeEventID: lastOutcomeEventID === null
      ? null
      : conversationIdentifier(
        lastOutcomeEventID,
        `${scope}.last_outcome_event_id`,
      ),
  };
}

function conversationEvent(
  value: unknown,
  scope: string,
  maximumRevision: number,
): AuthoritativeConversationEvent {
  const row = objectValue(value, scope);
  const action = row.action;
  if (
    action !== "revealed"
    && action !== "discussed"
    && action !== "postponed"
    && action !== "corrected"
    && action !== "ended"
  ) {
    throw new CloudflareRepositoryError(
      `${scope}.action is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  const assignmentID = nullableString(
    row.assignment_id,
    `${scope}.assignment_id`,
  );
  const targetEventID = nullableString(
    row.target_event_id,
    `${scope}.target_event_id`,
  );
  const event: AuthoritativeConversationEvent = {
    id: conversationIdentifier(row.id, `${scope}.id`),
    assignmentID: assignmentID === null
      ? null
      : conversationIdentifier(assignmentID, `${scope}.assignment_id`),
    action,
    targetEventID: targetEventID === null
      ? null
      : conversationIdentifier(targetEventID, `${scope}.target_event_id`),
    previousPosition: nullableInteger(
      row.previous_position,
      `${scope}.previous_position`,
    ),
    nextPosition: nullableInteger(
      row.next_position,
      `${scope}.next_position`,
    ),
    occurredAt: timestampValue(row.occurred_at, `${scope}.occurred_at`),
    revision: integerValue(row.revision, `${scope}.revision`),
  };
  if (event.revision > maximumRevision) {
    throw new CloudflareRepositoryError(
      `${scope}.revision is unavailable.`,
      502,
      "invalid_server_response",
    );
  }
  const validShape = action === "ended"
    ? event.assignmentID === null && event.targetEventID === null
    : action === "corrected"
      ? event.assignmentID !== null && event.targetEventID !== null
      : event.assignmentID !== null && event.targetEventID === null;
  if (!validShape) {
    throw new CloudflareRepositoryError(
      `${scope} is inconsistent.`,
      502,
      "invalid_server_response",
    );
  }
  return event;
}

function conversationSession(
  value: unknown,
  scope: string,
  maximumRevision: number,
): AuthoritativeConversationSession {
  const row = objectValue(value, scope);
  const revision = integerValue(row.revision, `${scope}.revision`);
  const startedAt = timestampValue(row.started_at, `${scope}.started_at`);
  const endedAt = nullableString(row.ended_at, `${scope}.ended_at`);
  const updatedAt = timestampValue(row.updated_at, `${scope}.updated_at`);
  const session: AuthoritativeConversationSession = {
    id: conversationIdentifier(row.id, `${scope}.id`),
    audienceID: conversationIdentifier(
      row.audience_id,
      `${scope}.audience_id`,
    ),
    revision,
    startedAt,
    endedAt: endedAt === null
      ? null
      : timestampValue(endedAt, `${scope}.ended_at`),
    endReason: conversationEndReason(row.end_reason, `${scope}.end_reason`),
    updatedAt,
    cards: (row.cards === undefined ? [] : arrayValue(row.cards, `${scope}.cards`))
      .map((card, index) => conversationCard(card, `${scope}.cards[${index}]`)),
    events: (
      row.events === undefined ? [] : arrayValue(row.events, `${scope}.events`)
    ).map((event, index) => (
      conversationEvent(event, `${scope}.events[${index}]`, revision)
    )),
  };
  if (
    revision > maximumRevision
    || Date.parse(updatedAt) < Date.parse(startedAt)
    || (
      session.endedAt === null
        ? session.endReason !== null
        : (
          session.endReason === null
          || Date.parse(session.endedAt) < Date.parse(startedAt)
          || Date.parse(updatedAt) < Date.parse(session.endedAt)
        )
    )
  ) {
    throw new CloudflareRepositoryError(
      `${scope} is inconsistent.`,
      502,
      "invalid_server_response",
    );
  }
  const assignmentIDs = new Set<string>();
  const positions = new Set<number>();
  for (const card of session.cards) {
    if (
      assignmentIDs.has(card.assignmentID)
      || positions.has(card.position)
    ) {
      throw new CloudflareRepositoryError(
        `${scope}.cards contained duplicate evidence.`,
        502,
        "invalid_server_response",
      );
    }
    assignmentIDs.add(card.assignmentID);
    positions.add(card.position);
  }
  const eventIDs = new Set<string>();
  let previousRevision = -1;
  for (const event of session.events) {
    if (
      eventIDs.has(event.id)
      || event.revision < previousRevision
    ) {
      throw new CloudflareRepositoryError(
        `${scope}.events contained duplicate or unordered evidence.`,
        502,
        "invalid_server_response",
      );
    }
    eventIDs.add(event.id);
    previousRevision = event.revision;
  }
  return session;
}

function conversationSessionsFromWire(
  value: unknown,
  response: Response,
): AuthoritativeConversationSessions {
  const body = objectValue(value, "Conversation sessions");
  rejectForbiddenKeysDeep(
    body,
    CONVERSATION_PRIVATE_KEYS,
    "Conversation sessions",
  );
  if (body.schema_version !== CONVERSATION_SCHEMA) {
    throw new CloudflareRepositoryError(
      "The Conversation session version is not supported.",
      502,
      "unsupported_schema_version",
    );
  }
  requirePrivateNoStore(response);
  const { etag, revision: etagRevision } = conversationETag(response);
  const revision = integerValue(body.revision, "Conversation sessions.revision");
  const sessionValues = arrayValue(
    body.sessions,
    "Conversation sessions.sessions",
  );
  if (revision !== etagRevision || sessionValues.length > 20) {
    throw new CloudflareRepositoryError(
      "The Conversation session list revision was inconsistent.",
      502,
      "invalid_server_response",
    );
  }
  const sessions = sessionValues.map((session, index) => (
    conversationSession(
      session,
      `Conversation sessions.sessions[${index}]`,
      revision,
    )
  ));
  const sessionIDs = new Set<string>();
  for (const session of sessions) {
    if (sessionIDs.has(session.id)) {
      throw new CloudflareRepositoryError(
        "The Conversation session list contained duplicate IDs.",
        502,
        "invalid_server_response",
      );
    }
    sessionIDs.add(session.id);
  }
  let cleanupRequiredSession: AuthoritativeConversationCleanupPointer | null =
    null;
  if (body.cleanup_required_session !== null) {
    const cleanup = objectValue(
      body.cleanup_required_session,
      "Conversation sessions.cleanup_required_session",
    );
    cleanupRequiredSession = {
      id: conversationIdentifier(
        cleanup.id,
        "Conversation sessions.cleanup_required_session.id",
      ),
      revision: integerValue(
        cleanup.revision,
        "Conversation sessions.cleanup_required_session.revision",
      ),
    };
    if (
      cleanupRequiredSession.revision > revision
      || sessionIDs.has(cleanupRequiredSession.id)
    ) {
      throw new CloudflareRepositoryError(
        "The Conversation cleanup pointer was inconsistent.",
        502,
        "invalid_server_response",
      );
    }
  }
  return {
    revision,
    etag,
    sessions,
    cleanupRequiredSession,
  };
}

function mutationResponseFromWire(
  value: unknown,
  response: Response,
  expected:
    | { kind: "start"; input: StartAuthoritativeConversationInput }
    | { kind: "command"; input: CommandAuthoritativeConversationInput },
): AuthoritativeConversationMutation {
  const body = objectValue(value, "Conversation mutation");
  rejectForbiddenKeysDeep(
    body,
    CONVERSATION_PRIVATE_KEYS,
    "Conversation mutation",
  );
  if (body.schema_version !== CONVERSATION_SCHEMA) {
    throw new CloudflareRepositoryError(
      "The Conversation mutation version is not supported.",
      502,
      "unsupported_schema_version",
    );
  }
  requirePrivateNoStore(response);
  const { etag, revision: etagRevision } = conversationETag(response);
  const revision = integerValue(body.revision, "Conversation mutation.revision");
  const session = conversationSession(
    body.session,
    "Conversation mutation.session",
    revision,
  );
  const event = body.event == null
    ? null
    : conversationEvent(
      body.event,
      "Conversation mutation.event",
      revision,
    );
  const card = body.card == null
    ? null
    : conversationCard(body.card, "Conversation mutation.card");
  if (
    revision !== etagRevision
    || session.revision !== revision
    || (event !== null && event.revision !== revision)
    || (event === null && card !== null)
    || (
      event !== null
      && event.action === "ended"
      && (event.assignmentID !== null || card !== null)
    )
    || (
      event !== null
      && event.action !== "ended"
      && (event.assignmentID === null || card?.assignmentID !== event.assignmentID)
    )
  ) {
    throw new CloudflareRepositoryError(
      "The accepted Conversation evidence was inconsistent.",
      502,
      "invalid_server_response",
    );
  }
  const mutation = { revision, etag, session, event, card };
  validateConversationMutationEvidence(mutation, expected);
  return mutation;
}

function validateConversationMutationEvidence(
  mutation: AuthoritativeConversationMutation,
  expected:
    | { kind: "start"; input: StartAuthoritativeConversationInput }
    | { kind: "command"; input: CommandAuthoritativeConversationInput },
): void {
  const fail = (): never => {
    throw new CloudflareRepositoryError(
      "The accepted Conversation evidence did not match this request.",
      502,
      "invalid_server_response",
    );
  };
  const { session, event, card } = mutation;
  if (expected.kind === "start") {
    const { audienceID, assignmentIDs } = expected.input;
    const orderedCards = [...session.cards].sort(
      (left, right) => left.position - right.position,
    );
    if (
      event !== null
      || card !== null
      || session.audienceID !== audienceID
      || session.endedAt !== null
      || session.endReason !== null
      || session.startedAt !== session.updatedAt
      || session.events.length !== 0
      || orderedCards.length !== assignmentIDs.length
      || orderedCards.some((candidate, index) => (
        candidate.assignmentID !== assignmentIDs[index]
        || candidate.position !== index
        || candidate.state !== "playable"
        || candidate.revealed
        || candidate.lastEventID !== null
        || candidate.lastOutcomeEventID !== null
      ))
    ) fail();
    return;
  }
  const input = expected.input;
  if (
    session.id !== input.sessionID
    || session.cards.length !== 0
    || session.events.length !== 0
  ) fail();
  if (event === null) fail();
  const acceptedEvent = event as AuthoritativeConversationEvent;
  if (input.action === "end") {
    if (
      input.assignmentID !== null
      || input.targetEventID !== null
      || input.endReason === null
      || session.endedAt === null
      || session.endReason !== input.endReason
      || session.updatedAt !== session.endedAt
      || acceptedEvent.action !== "ended"
      || acceptedEvent.assignmentID !== null
      || acceptedEvent.targetEventID !== null
      || acceptedEvent.previousPosition !== null
      || acceptedEvent.nextPosition !== null
      || acceptedEvent.occurredAt !== session.endedAt
      || card !== null
    ) fail();
    return;
  }
  if (
    input.assignmentID === null
    || input.endReason !== null
    || session.endedAt !== null
    || session.endReason !== null
    || acceptedEvent.previousPosition === null
    || acceptedEvent.nextPosition === null
  ) fail();
  if (card === null) fail();
  const acceptedCard = card as AuthoritativeConversationCard;
  if (
    acceptedEvent.assignmentID !== input.assignmentID
    || acceptedCard.assignmentID !== input.assignmentID
    || acceptedEvent.occurredAt !== session.updatedAt
    || acceptedCard.lastEventID !== acceptedEvent.id
    || acceptedCard.position !== acceptedEvent.nextPosition
  ) fail();
  const evidenceMatches = input.action === "reveal"
    ? (
      input.targetEventID === null
      && acceptedEvent.action === "revealed"
      && acceptedEvent.targetEventID === null
      && acceptedEvent.previousPosition === acceptedEvent.nextPosition
      && acceptedCard.state === "playable"
      && acceptedCard.revealed
      && acceptedCard.lastOutcomeEventID === null
    )
    : input.action === "discussed"
      ? (
        input.targetEventID === null
        && acceptedEvent.action === "discussed"
        && acceptedEvent.targetEventID === null
        && acceptedEvent.previousPosition === acceptedEvent.nextPosition
        && acceptedCard.state === "discussed"
        && !acceptedCard.revealed
        && acceptedCard.lastOutcomeEventID === acceptedEvent.id
      )
      : input.action === "postponed"
        ? (
          input.targetEventID === null
          && acceptedEvent.action === "postponed"
          && acceptedEvent.targetEventID === null
          && acceptedEvent.nextPosition! > acceptedEvent.previousPosition!
          && acceptedCard.state === "playable"
          && !acceptedCard.revealed
          && acceptedCard.lastOutcomeEventID === acceptedEvent.id
        )
        : (
          input.action === "correct"
          && input.targetEventID !== null
          && acceptedEvent.action === "corrected"
          && acceptedEvent.targetEventID === input.targetEventID
          && acceptedEvent.previousPosition === acceptedEvent.nextPosition
          && acceptedCard.state === "playable"
          && acceptedCard.revealed
          && acceptedCard.lastOutcomeEventID === null
        );
  if (!evidenceMatches) fail();
}

function validateConversationInputIdentifier(
  value: string,
  scope: string,
): void {
  if (!CONVERSATION_IDENTIFIER.test(value)) {
    throw new CloudflareRepositoryError(
      `${scope} is invalid.`,
      400,
      "invalid_conversation_request",
    );
  }
}

function validateConversationMutationHeaders(
  etag: string,
  idempotencyKey: string,
): string {
  const canonicalETag = etag.startsWith("W/") ? etag.slice(2) : etag;
  if (!CONVERSATION_ETAG.test(canonicalETag)) {
    throw new CloudflareRepositoryError(
      "Refresh Conversation Cards before continuing.",
      428,
      "conversation_if_match_required",
    );
  }
  if (!CONVERSATION_IDEMPOTENCY_KEY.test(idempotencyKey)) {
    throw new CloudflareRepositoryError(
      "The Conversation retry identity is invalid.",
      400,
      "invalid_idempotency_key",
    );
  }
  return canonicalETag;
}

export class CloudflareConversationSessionRepository
implements AuthoritativeConversationRepository {
  constructor(private readonly fetcher: Fetcher = globalThis.fetch.bind(
    globalThis,
  )) {}

  async load(audienceID?: string): Promise<AuthoritativeConversationSessions> {
    if (audienceID !== undefined) {
      validateConversationInputIdentifier(audienceID, "audience_id");
    }
    const suffix: `/${string}` = audienceID === undefined
      ? "/owner/conversation-sessions"
      : `/owner/conversation-sessions?audience_id=${
        encodeURIComponent(audienceID)
      }`;
    const response = await this.fetcher(apiRoute(suffix), {
      credentials: "same-origin",
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!response.ok) {
      throw await responseError(
        response,
        "Conversation Cards could not load.",
      );
    }
    return conversationSessionsFromWire(await jsonRecord(response), response);
  }

  async start(
    input: StartAuthoritativeConversationInput,
  ): Promise<AuthoritativeConversationMutation> {
    validateConversationInputIdentifier(input.audienceID, "audience_id");
    const requestETag = validateConversationMutationHeaders(
      input.etag,
      input.idempotencyKey,
    );
    if (
      input.assignmentIDs.length < 1
      || input.assignmentIDs.length > 10_000
      || new Set(input.assignmentIDs).size !== input.assignmentIDs.length
    ) {
      throw new CloudflareRepositoryError(
        "Choose between 1 and 10,000 distinct Conversation cards.",
        400,
        "invalid_conversation_manifest",
      );
    }
    input.assignmentIDs.forEach((assignmentID) => {
      validateConversationInputIdentifier(assignmentID, "assignment_id");
    });
    const response = await this.fetcher(
      apiRoute("/owner/conversation-sessions"),
      {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "if-match": requestETag,
          "idempotency-key": input.idempotencyKey,
        },
        body: JSON.stringify({
          audience_id: input.audienceID,
          assignment_ids: input.assignmentIDs,
        }),
      },
    );
    if (response.status !== 201) {
      throw await responseError(
        response,
        "The Conversation session could not start.",
      );
    }
    return mutationResponseFromWire(
      await jsonRecord(response),
      response,
      { kind: "start", input },
    );
  }

  async command(
    input: CommandAuthoritativeConversationInput,
  ): Promise<AuthoritativeConversationMutation> {
    validateConversationInputIdentifier(input.sessionID, "session_id");
    const requestETag = validateConversationMutationHeaders(
      input.etag,
      input.idempotencyKey,
    );
    if (
      input.action !== "reveal"
      && input.action !== "discussed"
      && input.action !== "postponed"
      && input.action !== "correct"
      && input.action !== "end"
    ) {
      throw new CloudflareRepositoryError(
        "The Conversation action is invalid.",
        400,
        "invalid_conversation_command",
      );
    }
    const body: JSONRecord = { action: input.action };
    if (input.action === "end") {
      if (
        input.assignmentID !== null
        || input.targetEventID !== null
        || input.endReason === null
        || (
          input.endReason !== "completed"
          && input.endReason !== "paused"
          && input.endReason !== "abandoned"
        )
      ) {
        throw new CloudflareRepositoryError(
          "The End command is invalid.",
          400,
          "invalid_conversation_command",
        );
      }
      body.end_reason = input.endReason;
    } else {
      if (input.assignmentID === null || input.endReason !== null) {
        throw new CloudflareRepositoryError(
          "The card command is invalid.",
          400,
          "invalid_conversation_command",
        );
      }
      validateConversationInputIdentifier(
        input.assignmentID,
        "assignment_id",
      );
      body.assignment_id = input.assignmentID;
      if (input.action === "correct") {
        if (input.targetEventID === null) {
          throw new CloudflareRepositoryError(
            "The correction target is invalid.",
            400,
            "invalid_conversation_command",
          );
        }
        validateConversationInputIdentifier(
          input.targetEventID,
          "target_event_id",
        );
        body.target_event_id = input.targetEventID;
      } else if (input.targetEventID !== null) {
        throw new CloudflareRepositoryError(
          "The card command included an unexpected correction target.",
          400,
          "invalid_conversation_command",
        );
      }
    }
    const response = await this.fetcher(
      apiRoute(
        `/owner/conversation-sessions/${encodeURIComponent(input.sessionID)}`,
      ),
      {
        method: "PATCH",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "if-match": requestETag,
          "idempotency-key": input.idempotencyKey,
        },
        body: JSON.stringify(body),
      },
    );
    if (response.status !== 200) {
      throw await responseError(
        response,
        "The Conversation action could not save.",
      );
    }
    return mutationResponseFromWire(
      await jsonRecord(response),
      response,
      { kind: "command", input },
    );
  }
}

export class CloudflareBookmarkRepository
implements BookmarkRepository, BookmarkCreateRepository {
  private cache: CachedOwnerWorkspace | null = null;

  constructor(private readonly fetcher: Fetcher = globalThis.fetch.bind(
    globalThis,
  )) {}

  private assignmentIDsExcluding(bookmarkID?: string): Set<string> {
    return new Set(
      (this.cache?.workspace.bookmarks ?? [])
        .filter(({ id }) => id !== bookmarkID)
        .flatMap(({ audienceAssignments }) => audienceAssignments)
        .filter(({ conversationEnabled, libraryEnabled }) => (
          conversationEnabled || libraryEnabled
        ))
        .map(({ id }) => id),
    );
  }

  async load(): Promise<WorkspaceLoadResult> {
    const headers = new Headers({ accept: "application/json" });
    if (this.cache) headers.set("if-none-match", this.cache.etag);
    const response = await this.fetcher(apiRoute("/owner/workspace"), {
      credentials: "same-origin",
      cache: "no-store",
      headers,
    });
    if (response.status === 304 && this.cache) {
      return {
        workspace: structuredClone(this.cache.workspace),
        context: structuredClone(this.cache.context),
      };
    }
    if (!response.ok) {
      throw await responseError(response, "The private workspace could not load.");
    }
    const etag = ownerWorkspaceETag(response);
    this.cache = ownerWorkspaceFromWire(await jsonRecord(response), etag);
    return {
      workspace: structuredClone(this.cache.workspace),
      context: structuredClone(this.cache.context),
    };
  }

  async saveBookmark(bookmark: Bookmark): Promise<BookmarkSaveResult> {
    if (!this.cache) {
      throw new CloudflareRepositoryError(
        "Refresh the private workspace before saving.",
        428,
        "workspace_not_loaded",
      );
    }
    const response = await this.fetcher(
      apiRoute(`/owner/bookmarks/${encodeURIComponent(bookmark.id)}`),
      {
        method: "PUT",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "if-match": this.cache.etag,
          "idempotency-key": `web-${crypto.randomUUID()}`,
        },
        body: JSON.stringify(ownerMutation(bookmark)),
      },
    );
    if (!response.ok) {
      throw await responseError(response, "The private bookmark could not save.");
    }
    const body = await jsonRecord(response);
    if (body.schema_version !== "owner-workspace-v1") {
      throw new CloudflareRepositoryError(
        "The saved bookmark version is not supported.",
        502,
        "unsupported_schema_version",
      );
    }
    const etag = ownerWorkspaceETag(response);
    const revision = integerValue(body.revision, "revision");
    const saved = bookmarkFromWire(
      body.bookmark,
      this.cache.context.ownerUserID,
      new Map(this.cache.context.audiences.map(({ id, label }) => [id, label])),
      0,
      this.assignmentIDsExcluding(bookmark.id),
    );
    const bookmarks = this.cache.workspace.bookmarks.map((candidate) => (
      candidate.id === saved.id ? saved : candidate
    ));
    const summaryEnrichmentJob = summaryEnrichmentResult(
      body,
      saved,
      revision,
    );
    const enrichmentJobs = [
      ...this.cache.workspace.enrichmentJobs.filter(
        ({ bookmarkId }) => bookmarkId !== saved.id,
      ),
      ...(summaryEnrichmentJob ? [summaryEnrichmentJob] : []),
    ];
    this.cache = {
      ...this.cache,
      revision,
      etag,
      workspace: { bookmarks, enrichmentJobs },
    };
    return {
      workspace: structuredClone(this.cache.workspace),
      summaryEnrichmentJob: summaryEnrichmentJob
        ? structuredClone(summaryEnrichmentJob)
        : null,
    };
  }

  async createBookmark(bookmark: Bookmark): Promise<BookmarkCreateResult> {
    if (!this.cache) {
      throw new CloudflareRepositoryError(
        "Refresh the private workspace before saving.",
        428,
        "workspace_not_loaded",
      );
    }
    const response = await this.fetcher(apiRoute("/owner/bookmarks"), {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        "if-match": this.cache.etag,
        "idempotency-key": ownerCreateIdempotencyKey(bookmark.id),
      },
      body: JSON.stringify(ownerCreateMutation(bookmark)),
    });
    if (!response.ok) {
      throw await responseError(
        response,
        "The New card could not save to your private workspace.",
      );
    }
    if (response.status !== 201) {
      throw new CloudflareRepositoryError(
        "Convoful returned an unexpected New card response.",
        502,
        "invalid_server_response",
      );
    }
    const body = await jsonRecord(response);
    if (body.schema_version !== "owner-workspace-v1") {
      throw new CloudflareRepositoryError(
        "The saved New card version is not supported.",
        502,
        "unsupported_schema_version",
      );
    }
    const etag = ownerWorkspaceETag(response);
    const revision = integerValue(body.revision, "revision");
    const savedBookmarkRow = objectValue(body.bookmark, "bookmark");
    const savedBookmarkID = nonEmptyString(savedBookmarkRow.id, "bookmark.id");
    const saved = bookmarkFromWire(
      savedBookmarkRow,
      this.cache.context.ownerUserID,
      new Map(this.cache.context.audiences.map(({ id, label }) => [id, label])),
      0,
      this.assignmentIDsExcluding(savedBookmarkID),
    );
    if (
      saved.id === bookmark.id
      || saved.id.startsWith("local-new-card-")
      || saved.audienceAssignments.some(({ id }) => id.startsWith("local-"))
      || saved.libraryPublications.some(({ id }) => id.startsWith("local-"))
    ) {
      throw new CloudflareRepositoryError(
        "The saved New card did not include canonical server identities.",
        502,
        "invalid_server_response",
      );
    }
    const summaryEnrichmentJob = summaryEnrichmentResult(
      body,
      saved,
      revision,
    );
    const bookmarks = [
      saved,
      ...this.cache.workspace.bookmarks.filter(
        ({ id }) => id !== saved.id,
      ),
    ];
    const enrichmentJobs = [
      ...this.cache.workspace.enrichmentJobs.filter(
        ({ bookmarkId }) => bookmarkId !== saved.id,
      ),
      ...(summaryEnrichmentJob ? [summaryEnrichmentJob] : []),
    ];
    this.cache = {
      ...this.cache,
      revision,
      etag,
      workspace: { bookmarks, enrichmentJobs },
    };
    return {
      workspace: structuredClone(this.cache.workspace),
      bookmark: structuredClone(saved),
      summaryEnrichmentJob: summaryEnrichmentJob
        ? structuredClone(summaryEnrichmentJob)
        : null,
    };
  }
}

function receiptFromWire(
  value: unknown,
  publicationID: string,
  viewerUserID: string,
  scope: string,
): { receipt: LibraryReceipt; transport: ReceiptTransportState } {
  const row = objectValue(value, scope);
  const nullableDate = (key: string): string | undefined => {
    const raw = nullableString(row[key], `${scope}.${key}`);
    return raw ?? undefined;
  };
  const etag = nonEmptyString(row.etag, `${scope}.etag`);
  const revision = integerValue(row.revision, `${scope}.revision`);
  return {
    receipt: {
      publicationId: publicationID,
      userId: viewerUserID,
      state: statusValue(row.status, `${scope}.status`),
      ...(nullableDate("opened_at")
        ? { openedAt: nullableDate("opened_at") }
        : {}),
      ...(nullableDate("progress_updated_at")
        ? { progressUpdatedAt: nullableDate("progress_updated_at") }
        : {}),
      ...(nullableDate("finished_at")
        ? { finishedAt: nullableDate("finished_at") }
        : {}),
      ...(nullableDate("dismissed_at")
        ? { dismissedAt: nullableDate("dismissed_at") }
        : {}),
      updatedAt: nonEmptyString(row.updated_at, `${scope}.updated_at`),
    },
    transport: { etag, revision },
  };
}

function recipientEntryFromWire(
  value: unknown,
  viewerUserID: string,
  index: number,
): { entry: LibraryRecipientEntry; transport: ReceiptTransportState } {
  const scope = `items[${index}]`;
  const row = objectValue(value, scope);
  rejectForbiddenKeysDeep(
    row,
    [
      "card_id",
      "canonical_card_id",
      "private_notes",
      "human_summary",
      "keywords",
      "tags",
      "payload",
      "summary_fingerprint",
      "summary_embedding",
    ],
    scope,
  );
  const publicationID = nonEmptyString(row.id, `${scope}.id`);
  const receipt = receiptFromWire(
    row.receipt,
    publicationID,
    viewerUserID,
    `${scope}.receipt`,
  );
  const videoDurationSeconds = optionalNonnegativeNumber(
    row.video_duration_seconds,
    `${scope}.video_duration_seconds`,
  );
  return {
    entry: {
      item: {
        id: publicationID,
        audienceId: nonEmptyString(
          row.audience_id,
          `${scope}.audience_id`,
        ),
        sharedByUserID: nonEmptyString(
          row.shared_by_user_id,
          `${scope}.shared_by_user_id`,
        ),
        title: nonEmptyString(row.title, `${scope}.title`),
        sourceURL: nullableString(row.source_url, `${scope}.source_url`),
        sourceLabel: stringValue(row.source_label, `${scope}.source_label`),
        sharedNotes: stringValue(row.shared_notes, `${scope}.shared_notes`),
        format: formatValue(row.format, `${scope}.format`),
        lane: laneValue(row.lane, `${scope}.lane`),
        ...(videoDurationSeconds === undefined
          ? {}
          : { videoDurationSeconds }),
        publishedAt: nonEmptyString(
          row.published_at,
          `${scope}.published_at`,
        ),
        updatedAt: nonEmptyString(row.updated_at, `${scope}.updated_at`),
      },
      receipt: receipt.receipt,
    },
    transport: receipt.transport,
  };
}

export class CloudflareLibraryRepository
implements RecipientLibraryRepository {
  private entries = new Map<string, LibraryRecipientEntry>();
  private transport = new Map<string, ReceiptTransportState>();

  constructor(
    private readonly viewerUserID: string,
    private readonly fetcher: Fetcher = globalThis.fetch.bind(globalThis),
  ) {
    if (!viewerUserID.trim()) {
      throw new Error("A current principal user ID is required.");
    }
  }

  async loadLibrary(): Promise<LibraryRecipientEntry[]> {
    const entries = new Map<string, LibraryRecipientEntry>();
    const transport = new Map<string, ReceiptTransportState>();
    const visitedCursors = new Set<string>();
    let cursor: string | null = null;
    do {
      const parameters = new URLSearchParams({ limit: "100" });
      if (cursor) parameters.set("cursor", cursor);
      const response = await this.fetcher(
        `${apiRoute("/library")}?${parameters.toString()}`,
        {
          credentials: "same-origin",
          cache: "no-store",
          headers: { accept: "application/json" },
        },
      );
      if (!response.ok) {
        throw await responseError(
          response,
          "The recipient Library could not load.",
        );
      }
      const body = await jsonRecord(response);
      if (body.schema_version !== "recipient-library-v1") {
        throw new CloudflareRepositoryError(
          "The recipient Library version is not supported.",
          502,
          "unsupported_schema_version",
        );
      }
      for (const [index, item] of arrayValue(body.items, "items").entries()) {
        const decoded = recipientEntryFromWire(
          item,
          this.viewerUserID,
          index,
        );
        if (entries.has(decoded.entry.item.id)) {
          throw new CloudflareRepositoryError(
            "The recipient Library returned a duplicate publication.",
            502,
            "duplicate_publication",
          );
        }
        entries.set(decoded.entry.item.id, decoded.entry);
        transport.set(decoded.entry.item.id, decoded.transport);
      }
      const hasMore = booleanValue(body.has_more, "has_more");
      const nextCursor = nullableString(body.next_cursor, "next_cursor");
      if (!hasMore) {
        cursor = null;
      } else if (!nextCursor || visitedCursors.has(nextCursor)) {
        throw new CloudflareRepositoryError(
          "The recipient Library cursor is invalid.",
          502,
          "invalid_cursor",
        );
      } else {
        visitedCursors.add(nextCursor);
        cursor = nextCursor;
      }
    } while (cursor);
    this.entries = entries;
    this.transport = transport;
    return structuredClone([...entries.values()]);
  }

  async loadReceipt(publicationID: string): Promise<LibraryReceipt | null> {
    if (!this.entries.has(publicationID)) await this.loadLibrary();
    const entry = this.entries.get(publicationID);
    return entry ? structuredClone(entry.receipt) : null;
  }

  async provisionReceipt(publicationID: string): Promise<LibraryReceipt> {
    const receipt = await this.loadReceipt(publicationID);
    if (!receipt) {
      throw new CloudflareRepositoryError(
        "This Library item is not available for the current recipient.",
        404,
        "not_found",
      );
    }
    return receipt;
  }

  async transitionReceipt(
    publicationID: string,
    state: LibraryStatus,
  ): Promise<LibraryReceipt> {
    const current = this.entries.get(publicationID)
      ?? (await this.loadLibrary()).find(({ item }) => (
        item.id === publicationID
      ));
    const transport = this.transport.get(publicationID);
    if (!current || !transport) {
      throw new CloudflareRepositoryError(
        "This Library item is not available for the current recipient.",
        404,
        "not_found",
      );
    }
    const response = await this.fetcher(
      apiRoute(`/library/${encodeURIComponent(publicationID)}/receipt`),
      {
        method: "PATCH",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "if-match": transport.etag,
          "idempotency-key": `web-${crypto.randomUUID()}`,
        },
        body: JSON.stringify({ status: state }),
      },
    );
    if (!response.ok) {
      throw await responseError(
        response,
        "Reading progress could not be saved.",
      );
    }
    const body = await jsonRecord(response);
    if (
      body.schema_version !== "recipient-library-v1"
      || body.publication_id !== publicationID
    ) {
      throw new CloudflareRepositoryError(
        "The saved receipt version is not supported.",
        502,
        "unsupported_schema_version",
      );
    }
    const decoded = receiptFromWire(
      body.receipt,
      publicationID,
      this.viewerUserID,
      "receipt",
    );
    this.entries.set(publicationID, {
      item: structuredClone(current.item),
      receipt: decoded.receipt,
    });
    this.transport.set(publicationID, decoded.transport);
    return structuredClone(decoded.receipt);
  }
}
